import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const pastaNoodleRecipes: RecipeFamily[] = [
  {
    id: "tomato-lentil-pasta",
    nameTemplate: "{vegetable.name} & {protein.name} pasta",
    translations: {
      tr: { nameTemplate: "{vegetable.name} ve {protein.name} makarna" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 30,
    slots: [
      slot("base", "Pasta", true, "pasta", [
        option("pasta", 180, "g", "dry", 3),
        option("noodles", 180, "g", "dry", 1),
      ]),
      slot("vegetable", "Sauce base", true, "tomato", [
        option("tomato", 450, "g", "crushed"),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 240, "g", "cooked and drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "finely diced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("spice", "Warm spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
      ]),
      slot("finish", "Finish", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3),
        option("basil", 15, "g", "torn", 2),
        option("feta", 60, "g", "crumbled", 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-base",
        template:
          "Cook {base.name} in water according to the package directions. Reserve 120 ml cooking water, then drain.",
        translations: {
          tr: {
            template:
              "Paketteki talimatlara göre {base.name} pişirin. 120 ml pişirme suyunu ayırın, ardından süzün.",
          },
        },
      },
      {
        id: "start-sauce",
        template:
          "Warm {oil.name} in a pan. Soften {onion.name} for 5 minutes, then add {garlic.name} and {spice.name} if using.",
        translations: {
          tr: {
            template:
              "Bir tavada {oil.name} ısıtın. {onion.name} 5 dakika yumuşayana kadar soteleyin, ardından {garlic.name} ve kullanıyorsanız {spice.name} ekleyin.",
          },
        },
      },
      {
        id: "simmer-sauce",
        template:
          "Add {vegetable.name}, {protein.name}, {salt.name}, and {black-pepper.name}. Simmer for 10 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {protein.name}, {salt.name} ve {black-pepper.name} ekleyin. 10 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Toss {base.name} through the sauce, adding reserved cooking water as needed. Add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} sosuyla karıştırın, gerekirse ayırdığınız pişirme suyunu ekleyin. İsterseniz {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "umami", "aroma"],
    editorialPriority: 10,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "miso-mushroom-noodles",
    nameTemplate: "Miso {vegetable.name} & {protein.name} noodles",
    translations: {
      tr: { nameTemplate: "Misolu {vegetable.name} ve {protein.name} noodle" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 16,
    totalMinutes: 24,
    slots: [
      slot("base", "Noodles", true, "noodles", [
        option("noodles", 180, "g", "dry", 3),
        option("pasta", 180, "g", "dry", 1),
      ]),
      slot("vegetable", "Umami vegetable", true, "mushroom", [
        option("mushroom", 350, "g", "thinly sliced", 3),
        option("eggplant", 350, "g", "cut into small cubes", 1),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained", 1),
      ]),
      slot("sauce", "Umami sauce", true, "miso", [
        option("miso", 1, "tbsp", undefined, 3),
        option("soy-sauce", 2, "tbsp", undefined, 2),
      ]),
      slot("ginger", "Aromatic", true, "ginger", [
        option("ginger", 15, "g", "finely grated", 3),
        option("garlic", 2, "piece", "finely chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("green", "Fresh green", false, "spinach", [
        option("spinach", 120, "g", "roughly chopped"),
      ]),
      slot("finish", "Finish", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch", undefined, 3),
        option("scallion", 2, "piece", "sliced", 2),
        option("sesame-seeds", 1, "tbsp", undefined, 1),
        option("pumpkin-seeds", 2, "tbsp", undefined, 1),
      ]),
      pantry.water(90),
    ],
    steps: [
      {
        id: "cook-noodles",
        template:
          "Cook {base.name} according to the package directions, then drain.",
        translations: {
          tr: {
            template:
              "{base.name} paketteki talimatlara göre pişirin, ardından süzün.",
          },
        },
      },
      {
        id: "brown",
        template:
          "Heat {oil.name} in a wide pan. Brown {protein.name} and {vegetable.name} for 7 to 8 minutes.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} ısıtın. {protein.name} ve {vegetable.name} 7-8 dakika kızartın.",
          },
        },
      },
      {
        id: "make-sauce",
        template:
          "Mix {sauce.name}, {ginger.name}, and {water.name}. Pour into the pan and add {green.name} if using.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {ginger.name} ve {water.name} karıştırın. Tavaya dökün ve kullanıyorsanız {green.name} ekleyin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name} and toss until glossy and hot. Finish with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} ekleyin ve parlak ve sıcak olana kadar karıştırın. İsterseniz {finish.name} ile tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "umami", "aroma", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "peanut-tofu-noodles",
    nameTemplate: "Creamy peanut {protein.name} noodles",
    translations: {
      tr: { nameTemplate: "Kremalı fıstıklı {protein.name} noodle" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 25,
    slots: [
      slot("base", "Noodles", true, "noodles", [
        option("noodles", 180, "g", "dry"),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained and dried", 1),
      ]),
      slot("vegetable", "Crunchy vegetable", true, "cabbage", [
        option("cabbage", 250, "g", "thinly sliced", 3),
        option("broccoli", 280, "g", "cut into small florets", 2),
      ]),
      slot("sauce", "Creamy sauce", true, "peanut-butter", [
        option("peanut-butter", 3, "tbsp"),
      ]),
      slot("seasoning", "Savory seasoning", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 2),
        option("lime", 1, "piece", "juiced", 1),
      ]),
      slot("ginger", "Aromatic", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch", undefined, 3),
        option("scallion", 2, "piece", "sliced", 2),
        option("cilantro", 15, "g", "chopped", 1),
      ]),
      pantry.water(120),
    ],
    steps: [
      {
        id: "cook-noodles",
        template:
          "Cook {base.name} according to the package directions, then drain.",
        translations: {
          tr: {
            template:
              "{base.name} paketteki talimatlara göre pişirin, ardından süzün.",
          },
        },
      },
      {
        id: "mix-sauce",
        template:
          "Whisk {sauce.name}, {seasoning.name}, {acid.name}, {ginger.name}, and {water.name} until smooth.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {seasoning.name}, {acid.name}, {ginger.name} ve {water.name} pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "cook-filling",
        template:
          "Heat {oil.name} in a wide pan. Brown {protein.name} for 5 minutes, then add {vegetable.name} and cook for 3 minutes.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} ısıtın. {protein.name} 5 dakika kızartın, ardından {vegetable.name} ekleyip 3 dakika pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name} and the sauce. Toss until hot and evenly coated, then add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} ve sosu ekleyin. Sıcak ve eşit kaplana kadar karıştırın, ardından kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "heat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "cold-sesame-noodles",
    nameTemplate: "Cold sesame {base.name} with {protein.name}",
    translations: {
      tr: { nameTemplate: "Soğuk susamlı {base.name} ve {protein.name}" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Noodles", true, "noodles", [
        option("noodles", 180, "g", "cooked and cooled", 3),
        option("pasta", 180, "g", "cooked and cooled", 1),
      ]),
      slot("sauce", "Creamy sauce", true, "tahini", [
        option("tahini", 3, "tbsp", undefined, 3),
        option("peanut-butter", 3, "tbsp", undefined, 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 200, "g", "drained", 3),
        option("tofu", 200, "g", "pressed and thinly sliced", 2),
      ]),
      slot("vegetable", "Crunchy vegetable", true, "cucumber", [
        option("cucumber", 200, "g", "cut into matchsticks", 3),
        option("carrot", 200, "g", "coarsely grated", 2),
      ]),
      slot("seasoning", "Savory seasoning", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("ginger", "Aromatic", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("finish", "Finish", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch", undefined, 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
    ],
    steps: [
      {
        id: "make-sauce",
        template:
          "Whisk {sauce.name}, {seasoning.name}, {acid.name}, and {ginger.name} until smooth.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {seasoning.name}, {acid.name} ve {ginger.name} pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name}, {protein.name}, and {vegetable.name}. Toss until evenly coated.",
        translations: {
          tr: {
            template:
              "{base.name}, {protein.name} ve {vegetable.name} ekleyin. Eşit kaplana kadar karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template: "Scatter {finish.name} over the top if using.",
        translations: {
          tr: { template: "İsterseniz üzerine {finish.name} serpin." },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "umami", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "lemon-herb-pasta-salad",
    nameTemplate: "{base.name} salad with {protein.name} & {herb.name}",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {herb.name} ile {base.name} salatası",
      },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 15,
    slots: [
      slot("base", "Pasta or noodles", true, "pasta", [
        option("pasta", 300, "g", "cooked and cooled", 3),
        option("noodles", 250, "g", "cooked and cooled", 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("black-beans", 240, "g", "drained", 2),
        option("lentils", 220, "g", "cooked and drained", 1),
      ]),
      slot("vegetable", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 250, "g", "diced", 3),
        option("tomato", 250, "g", "diced", 2),
        option("bell-pepper", 250, "g", "diced", 1),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "finely chopped", 3),
        option("cilantro", 20, "g", "finely chopped", 2),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 2, "tbsp", undefined, 2),
      ]),
      slot("oil", "Dressing fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      slot("crunch", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {acid.name}, {oil.name}, {salt.name}, and {black-pepper.name} in a large bowl.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {acid.name}, {oil.name}, {salt.name} ve {black-pepper.name} çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name}, {protein.name}, {vegetable.name}, and {herb.name}. Toss until evenly dressed.",
        translations: {
          tr: {
            template:
              "{base.name}, {protein.name}, {vegetable.name} ve {herb.name} ekleyin. Eşit soslanana kadar karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Taste and adjust seasoning. Top with {crunch.name} if using.",
        translations: {
          tr: {
            template:
              "Tadına bakın ve baharatı ayarlayın. İsterseniz üzerine {crunch.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "fat", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "vegetable-pasta-bake",
    nameTemplate: "{vegetable.name} & {protein.name} pasta bake",
    translations: {
      tr: {
        nameTemplate: "Fırında {vegetable.name} ve {protein.name} makarna",
      },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 45,
    slots: [
      slot("base", "Pasta", true, "pasta", [option("pasta", 160, "g", "dry")]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("vegetable", "Vegetable", true, "zucchini", [
        option("zucchini", 300, "g", "cut into half-moons", 3),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 2),
        option("bell-pepper", 300, "g", "cut into strips", 1),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 240, "g", "cooked and drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("top", "Topping", false, "bread", [
        option("bread", 60, "g", "torn into rough crumbs", 3),
        option("pumpkin-seeds", 3, "tbsp", undefined, 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template:
          "Heat the oven to 200°C. Cook {base.name} in boiling salted water for 3 minutes less than the package directions, then drain.",
        translations: {
          tr: {
            template:
              "Fırını 200°C'ye önceden ısıtın. {base.name} tuzlu kaynar suda paketteki süreden 3 dakika kısa pişirin, ardından süzün.",
          },
        },
      },
      {
        id: "build",
        template:
          "Combine {sauce.name}, {garlic.name}, {protein.name}, {vegetable.name}, {oil.name}, {salt.name}, and {black-pepper.name} in a baking dish. Fold in {base.name}.",
        translations: {
          tr: {
            template:
              "Fırın kabında {sauce.name}, {garlic.name}, {protein.name}, {vegetable.name}, {oil.name}, {salt.name} ve {black-pepper.name} birleştirin. {base.name} katlayın.",
          },
        },
      },
      {
        id: "top",
        template: "Scatter {top.name} over the surface if using.",
        translations: {
          tr: { template: "İsterseniz yüzeye {top.name} serpin." },
        },
      },
      {
        id: "bake",
        template:
          "Bake for 20 to 25 minutes, until the sauce is bubbling and the top is lightly browned.",
        translations: {
          tr: {
            template:
              "Sos kabarmaya ve üstü hafifçe kızarana kadar 20-25 dakika fırınlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "umami", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "baked-mushroom-lentil-pasta",
    nameTemplate: "Baked {vegetable.name} & {protein.name} pasta",
    translations: {
      tr: {
        nameTemplate: "Fırında {vegetable.name} ve {protein.name} makarna",
      },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 40,
    slots: [
      slot("base", "Pasta", true, "pasta", [
        option("pasta", 250, "g", "cooked and drained", 3),
        option("noodles", 200, "g", "cooked and drained", 2),
      ]),
      slot("vegetable", "Vegetable", true, "mushroom", [
        option("mushroom", 300, "g", "thinly sliced", 3),
        option("bell-pepper", 300, "g", "cut into strips", 2),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 250, "g", "cooked and drained", 3),
        option("black-beans", 240, "g", "drained", 2),
        option("chickpeas", 240, "g", "drained", 1),
      ]),
      slot("sauce", "Sauce", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Warm spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye önceden ısıtın." } },
      },
      {
        id: "build",
        template:
          "Combine {sauce.name}, {garlic.name}, {vegetable.name}, {protein.name}, {oil.name}, {spice.name} if using, {salt.name}, and {black-pepper.name} in a large baking dish. Stir well.",
        translations: {
          tr: {
            template:
              "Büyük bir fırın kabında {sauce.name}, {garlic.name}, {vegetable.name}, {protein.name}, {oil.name}, kullanıyorsanız {spice.name}, {salt.name} ve {black-pepper.name} birleştirin. İyice karıştırın.",
          },
        },
      },
      {
        id: "fold-pasta",
        template: "Fold {base.name} into the sauce mixture, pushing it under.",
        translations: {
          tr: {
            template: "{base.name} sos karışımına katlayın, altına bastırın.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Bake for 20 minutes, until the top is starting to brown and the sauce is bubbling. Scatter {herb.name} if using and serve.",
        translations: {
          tr: {
            template:
              "Üstü kızarmaya başlayana ve sos kabarmaya kadar 20 dakika fırınlayın. İsterseniz {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "umami", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-tomato-onion-pasta",
    nameTemplate: "{protein.name} & {sauce.name} pasta",
    translations: {
      tr: { nameTemplate: "{protein.name} ve {sauce.name} makarna" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 30,
    slots: [
      slot("base", "Pasta", true, "pasta", [
        option("pasta", 180, "g", "dry", 3),
        option("noodles", 180, "g", "dry", 1),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and crumbled", 3),
        option("lentils", 240, "g", "cooked and drained", 2),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 450, "g", "crushed"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "finely diced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Warm spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "basil", [
        option("basil", 15, "g", "torn", 3),
        option("parsley", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-pasta",
        template:
          "Cook {base.name} in salted water according to the package directions. Reserve 120 ml cooking water, then drain.",
        translations: {
          tr: {
            template:
              "{base.name} tuzlu suda paketteki talimatlara göre pişirin. 120 ml pişirme suyunu ayırın, ardından süzün.",
          },
        },
      },
      {
        id: "cook-tofu",
        template:
          "Warm {oil.name} in a wide pan over medium-high heat. Add {protein.name} and cook for 5 minutes, stirring occasionally, until golden and slightly crispy.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} orta-yüksek ateşte ısıtın. {protein.name} ekleyin ve ara sıra karıştırarak altın rengi ve hafifçe çıtır olana kadar 5 dakika pişirin.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Push the {protein.name} to one side. Add {onion.name} and cook for 4 minutes until soft. Stir in {garlic.name} and {spice.name} if using for 30 seconds, then add {sauce.name}, {salt.name}, and {black-pepper.name}. Simmer for 8 minutes.",
        translations: {
          tr: {
            template:
              "{protein.name} bir kenara itin. {onion.name} ekleyin ve yumuşayana kadar 4 dakika pişirin. {garlic.name} ve kullanıyorsanız {spice.name} ekleyip 30 saniye karıştırın, ardından {sauce.name}, {salt.name} ve {black-pepper.name} ekleyin. 8 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Toss {base.name} through the sauce, adding reserved cooking water as needed to loosen. Scatter {herb.name} if using and serve.",
        translations: {
          tr: {
            template:
              "{base.name} sosuyla karıştırın, gerekirse sosu açmak için ayırdığınız pişirme suyunu ekleyin. İsterseniz {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "umami", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "garlic-spinach-chickpea-pasta",
    nameTemplate: "Garlicky {greens.name} & {protein.name} pasta",
    translations: {
      tr: {
        nameTemplate: "Sarımsaklı {greens.name} ve {protein.name} makarna",
      },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 22,
    slots: [
      slot("base", "Pasta", true, "pasta", [
        option("pasta", 200, "g", "dried"),
      ]),
      slot("greens", "Greens", true, "spinach", [
        option("spinach", 200, "g", "roughly chopped", 3),
        option("broccoli", 300, "g", "cut into small florets", 2),
        option("cabbage", 250, "g", "thinly sliced", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("white-beans", 240, "g", "drained", 2),
        option("lentils", 200, "g", "cooked", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 4, "piece", "thinly sliced"),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("basil", 15, "g", "torn", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-pasta",
        template:
          "Cook {base.name} in well-salted boiling water until al dente. Reserve 120 ml of pasta water, then drain.",
        translations: {
          tr: {
            template:
              "{base.name} bol tuzlu kaynar suda al dente'ye kadar pişirin. 120 ml makarna suyunu ayırın, ardından süzün.",
          },
        },
      },
      {
        id: "soften-garlic",
        template:
          "Warm {oil.name} in a wide pan over medium-low heat. Add {garlic.name} and {heat.name} if using; cook gently for 2 minutes until fragrant but not browned.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} orta-düşük ateşte ısıtın. {garlic.name} ve kullanıyorsanız {heat.name} ekleyin; kokusu çıkana ama kızarmadan 2 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "add-greens-protein",
        template:
          "Add {greens.name} to the pan and toss for 2 minutes until just wilted. Stir in {protein.name} and heat for 1 minute.",
        translations: {
          tr: {
            template:
              "Tavaya {greens.name} ekleyin ve 2 dakika hafifçe solana kadar karıştırın. {protein.name} ekleyip 1 dakika ısıtın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add the drained pasta, {acid.name} juice, {salt.name}, and {black-pepper.name}. Toss well, adding pasta water to loosen. Scatter {herb.name} if using and serve.",
        translations: {
          tr: {
            template:
              "Süzülmüş makarnayı, {acid.name} suyunu, {salt.name} ve {black-pepper.name} ekleyin. İyice karıştırın, sosu açmak için makarna suyu ekleyin. İsterseniz {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "bell-pepper-chickpea-pasta",
    nameTemplate: "{vegetable.name} & {protein.name} pasta",
    translations: {
      tr: { nameTemplate: "{vegetable.name} ve {protein.name} makarna" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("pasta", "Pasta", true, "pasta", [
        option("pasta", 200, "g", "penne or rigatoni", 3),
        option("spaghetti", 200, "g", undefined, 2),
      ]),
      slot("vegetable", "Main vegetable", true, "bell-pepper", [
        option("bell-pepper", 3, "piece", "deseeded and sliced", 3),
        option("zucchini", 350, "g", "cut into half-moons", 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("white-beans", 240, "g", "drained", 2),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 300, "g", "crushed", 3),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely sliced"),
      ]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1.5, "tsp", undefined, 3),
        option("cumin", 1.5, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "basil", [
        option("basil", 10, "g", "torn", 3),
        option("parsley", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-pasta",
        template:
          "Cook {pasta.name} in well-salted boiling water until al dente. Reserve 100 ml pasta water, then drain.",
        translations: {
          tr: {
            template:
              "{pasta.name} bol tuzlu kaynar suda al dente'ye kadar pişirin. 100 ml makarna suyunu ayırın, ardından süzün.",
          },
        },
      },
      {
        id: "char-veg",
        template:
          "Heat {oil.name} in a large frying pan over high heat. Add {vegetable.name} and cook without stirring for 3 minutes until charred at the edges. Stir and cook 2 minutes more.",
        translations: {
          tr: {
            template:
              "Büyük bir tavada {oil.name} yüksek ateşte ısıtın. {vegetable.name} ekleyin ve 3 dakika karıştırmadan kenarları karamelleşene kadar pişirin. Karıştırıp 2 dakika daha pişirin.",
          },
        },
      },
      {
        id: "sauce",
        template:
          "Reduce heat to medium. Add {garlic.name} and {spice.name}; stir 1 minute. Add {protein.name} and {sauce.name}; season with {salt.name} and {black-pepper.name}. Cook for 5 minutes.",
        translations: {
          tr: {
            template:
              "Ateşi orta dereceye düşürün. {garlic.name} ve {spice.name} ekleyin; 1 dakika karıştırın. {protein.name} ve {sauce.name} ekleyin; {salt.name} ve {black-pepper.name} ile tatlandırın. 5 dakika pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add drained {pasta.name} to the pan with a splash of reserved pasta water. Toss to coat, adding more water as needed. Scatter over {herb.name} if using and serve.",
        translations: {
          tr: {
            template:
              "Süzülmüş {pasta.name} ve biraz ayırdığınız makarna suyu tavaya ekleyin. Kaplayana kadar karıştırın, gerekirse daha fazla su ekleyin. İsterseniz üzerine {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "umami", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "olive-red-pepper-pasta",
    nameTemplate: "{topping.name} & {sauce.name} pasta",
    translations: {
      tr: { nameTemplate: "{topping.name} ve {sauce.name} makarna" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("pasta", "Pasta", true, "pasta", [
        option("pasta", 200, "g", "penne or rigatoni", 3),
        option("spaghetti", 200, "g", undefined, 2),
      ]),
      slot("topping", "Savoury topping", true, "olives", [
        option("olives", 80, "g", "pitted and halved", 3),
        option("sun-dried-tomato", 60, "g", "roughly chopped", 2),
      ]),
      slot("sauce", "Spiced paste", true, "red-pepper-paste", [
        option("red-pepper-paste", 2, "tbsp", undefined, 3),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("tomato", "Tomato base", true, "tomato", [
        option("tomato", 300, "g", "crushed", 3),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 4, "piece", "finely sliced"),
      ]),
      slot("spice", "Heat", false, "chili-flakes", [
        option("chili-flakes", 0.5, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("basil", 10, "g", "torn", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-pasta",
        template:
          "Cook {pasta.name} in well-salted boiling water until al dente. Reserve 100 ml pasta water, then drain.",
        translations: {
          tr: {
            template:
              "{pasta.name} bol tuzlu kaynar suda al dente'ye kadar pişirin. 100 ml makarna suyunu ayırın, ardından süzün.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Heat {oil.name} in a large frying pan over medium heat. Add {garlic.name} and {spice.name} if using; cook 1 minute until fragrant. Add {sauce.name} and stir for 1 minute.",
        translations: {
          tr: {
            template:
              "Büyük bir tavada {oil.name} orta ateşte ısıtın. {garlic.name} ve kullanıyorsanız {spice.name} ekleyin; kokusu çıkana kadar 1 dakika pişirin. {sauce.name} ekleyin ve 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {tomato.name} and {topping.name}. Season with {salt.name} and {black-pepper.name}. Simmer 8 minutes until the sauce thickens and the {topping.name} is warmed through.",
        translations: {
          tr: {
            template:
              "{tomato.name} ve {topping.name} ekleyin. {salt.name} ve {black-pepper.name} ile tatlandırın. Sos koyulaşana ve {topping.name} ısınana kadar 8 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add drained {pasta.name} with a splash of reserved pasta water; toss to coat. Scatter over {herb.name} if using and serve.",
        translations: {
          tr: {
            template:
              "Süzülmüş {pasta.name} ve biraz ayırdığınız makarna suyunu ekleyin; kaplayana kadar karıştırın. İsterseniz üzerine {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "umami", "fat", "acid", "aroma", "salt"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "leek-white-bean-pasta",
    nameTemplate: "{allium.name} & {legume.name} pasta",
    translations: {
      tr: { nameTemplate: "{allium.name} ve {legume.name} makarna" },
    },
    foodType: "pasta-noodles",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("pasta", "Pasta", true, "pasta", [
        option("pasta", 200, "g", undefined, 3),
        option("spaghetti", 200, "g", undefined, 2),
      ]),
      slot("allium", "Allium", true, "leek", [
        option("leek", 2, "piece", "sliced into thin rounds", 3),
        option("fennel", 1, "piece", "thinly sliced", 2),
        option("onion", 1, "piece", "thinly sliced", 1),
      ]),
      slot("legume", "Legume", true, "white-beans", [
        option("white-beans", 240, "g", "drained and rinsed", 3),
        option("chickpeas", 240, "g", "drained and rinsed", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely sliced"),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "zested and juiced", 3),
        option("red-wine-vinegar", 1, "tbsp", undefined, 1),
      ]),
      slot("herb", "Herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("dill", 10, "g", "roughly chopped", 2),
        option("basil", 10, "g", "torn", 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
      pantry.water(80),
    ],
    steps: [
      {
        id: "cook-pasta",
        template:
          "Cook {pasta.name} in well-salted boiling water until al dente. Reserve 80 ml pasta water, then drain.",
        translations: {
          tr: {
            template:
              "{pasta.name} bol tuzlu kaynar suda al dente'ye kadar pişirin. 80 ml makarna suyunu ayırın, ardından süzün.",
          },
        },
      },
      {
        id: "soften-allium",
        template:
          "Heat {oil.name} in a wide pan over medium heat. Add {allium.name} and a pinch of salt and cook for 8–10 minutes, stirring occasionally, until soft and lightly golden.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} orta ateşte ısıtın. {allium.name} ve bir tutam tuz ekleyin; ara sıra karıştırarak yumuşak ve hafifçe altın rengi olana kadar 8-10 dakika pişirin.",
          },
        },
      },
      {
        id: "add-garlic-beans",
        template:
          "Add {garlic.name} and cook 1 minute, then stir in {legume.name} and {water.name}. Simmer for 3 minutes until beans are warmed through.",
        translations: {
          tr: {
            template:
              "{garlic.name} ekleyin ve 1 dakika pişirin, ardından {legume.name} ve {water.name} ekleyin. Fasulyelerin ısınana kadar 3 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add drained {pasta.name} and {acid.name}, toss well, and season with {salt.name} and {black-pepper.name}. Scatter {herb.name} over the top if using and serve.",
        translations: {
          tr: {
            template:
              "Süzülmüş {pasta.name} ve {acid.name} ekleyin, iyice karıştırın ve {salt.name} ve {black-pepper.name} ile tatlandırın. İsterseniz üzerine {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "aroma", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
];
